// Config for the 3 dedicated bespoke configurator pages (/bespoke/shirt, /bespoke/suit,
// /bespoke/trousers) and the /bespoke hub. Each page owns its own step-by-step flow;
// this file holds the shared descriptive content (gallery, pricing copy, FAQs, hub card).

export const BESPOKE_CATEGORIES = {
  shirt: {
    key: "shirt",
    garmentKey: "shirt",
    label: "Shirt",
    hubLabel: "Custom Shirts",
    hubSubtitle: "Made just for you with premium fabrics and a flawless fit.",
    hubImage: "https://images.unsplash.com/photo-1507680434567-5739c80be1ac?crop=entropy&cs=srgb&fm=jpg",
    title: "Customised Men's Shirt — Tailored Just for You",
    tagline: "Cut and stitched to your exact measurements, in a fabric you choose.",
    images: [
      "https://images.unsplash.com/photo-1507680434567-5739c80be1ac?crop=entropy&cs=srgb&fm=jpg",
      "https://images.unsplash.com/photo-1591944489410-16ec1074a18e?crop=entropy&cs=srgb&fm=jpg",
    ],
    rating: 4.9,
    ratingCount: 128,
    price: 3500,
    sizes: ["S", "M", "L", "XL", "XXL", "3XL"],
    highlights: [
      "Hand-finished collar & cuff",
      "Choice of 30+ shirting fabrics",
      "Fit guaranteed — free first alteration",
    ],
    deliveryDays: "8 – 10 days",
    description: [
      "Every SquireCouture shirt starts as a length of fabric and a set of measurements — yours, and no one else's. We cut, stitch and finish each piece by hand in our Hubli atelier, so the collar sits right and the cuff meets your wrist exactly where it should.",
      "Choose from formal cottons, breathable linens and weekend-ready checks, then set the fit, collar and cuff to match how you actually dress. It's a shirt built around you, not the other way round.",
    ],
    faqs: [
      { q: "How long does a bespoke shirt take?", a: "8–10 days from measurement confirmation, ready to wear on delivery." },
      { q: "What if the fit isn't right first time?", a: "Your first alteration is free — send it back within 7 days of delivery." },
      { q: "Can I reorder the same shirt later?", a: "Yes — we keep your measurement profile on file for quick reorders." },
    ],
  },
  suit: {
    key: "suit",
    garmentKey: "suit",
    label: "Suit",
    hubLabel: "Custom Suits",
    hubSubtitle: "A suit stitched to your measurements — look confident, feel comfortable.",
    hubImage: "https://images.unsplash.com/photo-1591944489410-16ec1074a18e?crop=entropy&cs=srgb&fm=jpg",
    title: "Customised Men's Suit — Tailored Just for You",
    tagline: "A jacket and trouser cut as one silhouette, built to your frame.",
    images: [
      "https://images.unsplash.com/photo-1591944489410-16ec1074a18e?crop=entropy&cs=srgb&fm=jpg",
      "https://images.unsplash.com/photo-1507680434567-5739c80be1ac?crop=entropy&cs=srgb&fm=jpg",
    ],
    rating: 4.9,
    ratingCount: 84,
    price: 22000,
    sizes: ["38", "40", "42", "44", "46", "48"],
    highlights: [
      "Full or half canvas construction",
      "Jacket & trouser cut as one silhouette",
      "Complimentary fitting session at our Hubli atelier",
    ],
    deliveryDays: "18 – 21 days",
    description: [
      "A suit is the one garment that has to work in every direction — sitting, standing, reaching for a handshake. Ours are built on a canvas structure that moves with you, in fabric weights chosen for Indian weather and occasions.",
      "From notch to peak lapel, single to double vent, every structural choice is yours. We finish with a complimentary in-atelier fitting before your suit ships.",
    ],
    faqs: [
      { q: "How long does a bespoke suit take?", a: "Typically 18–21 days from measurement confirmation. We back-calculate from your event date." },
      { q: "Can I get a trial fitting?", a: "Yes — book a complimentary fitting session at our Hubli atelier before final stitching." },
      { q: "Full canvas or half canvas — what's the difference?", a: "Full canvas drapes and ages better over years of wear; half canvas is lighter and more affordable. We'll help you choose during fabric selection." },
    ],
  },
  trousers: {
    key: "trousers",
    garmentKey: "trousers",
    label: "Trousers",
    hubLabel: "Custom Pants",
    hubSubtitle: "Tailored to your exact size for all-day comfort and a sharp look.",
    hubImage: "https://images.unsplash.com/photo-1617114919297-3c8ddb01f599?crop=entropy&cs=srgb&fm=jpg",
    title: "Customised Men's Trousers — Tailored Just for You",
    tagline: "The drape you never quite get off the rack.",
    images: [
      "https://images.unsplash.com/photo-1617114919297-3c8ddb01f599?crop=entropy&cs=srgb&fm=jpg",
      "https://images.pexels.com/photos/6766286/pexels-photo-6766286.jpeg",
    ],
    rating: 4.8,
    ratingCount: 96,
    price: 4500,
    sizes: ["30", "32", "34", "36", "38", "40"],
    highlights: [
      "Flat-front or pleated, your call",
      "Comfort waistband, cut to your seat & thigh",
      "Cuffed or plain hem finishing",
    ],
    deliveryDays: "10 – 12 days",
    description: [
      "Trousers live or die by the seat and thigh measurement — the part every off-the-rack pair gets a little wrong. We take those numbers directly from you, so the waistband sits flat and the leg breaks exactly where it should.",
      "Pick flat-front or pleated, a plain or cuffed hem, and a fabric weight suited to the season you'll wear them in most.",
    ],
    faqs: [
      { q: "How long do bespoke trousers take?", a: "10–12 days from measurement confirmation." },
      { q: "Can I order trousers to match an existing jacket?", a: "Yes — mention the fabric or jacket order ID in special requirements and we'll match it." },
      { q: "Do you offer a stretch waistband option?", a: "Yes, available as a style option during fabric selection." },
    ],
  },
  kurta: {
    key: "kurta",
    garmentKey: "kurta",
    label: "Kurta",
    hubLabel: "Custom Kurtas",
    hubSubtitle: "Festive and everyday kurtas, cut to your measurements.",
    hubImage: "https://images.unsplash.com/photo-1595341888016-a392ef81b7de?crop=entropy&cs=srgb&fm=jpg",
    title: "Customised Men's Kurta — Tailored Just for You",
    tagline: "Traditional cut, tailored fit.",
    images: [
      "https://images.unsplash.com/photo-1595341888016-a392ef81b7de?crop=entropy&cs=srgb&fm=jpg",
      "https://images.unsplash.com/photo-1617114919297-3c8ddb01f599?crop=entropy&cs=srgb&fm=jpg",
    ],
    rating: 4.8,
    ratingCount: 64,
    price: 5500,
    sizes: ["S", "M", "L", "XL", "XXL", "3XL"],
    highlights: [
      "Choice of collar and placket finish",
      "Straight or angled side slits",
      "Festive silks and everyday cottons",
    ],
    deliveryDays: "10 – 12 days",
    description: [
      "A kurta should fall clean from the shoulder without pulling at the chest or bunching at the hip. We cut each one to your measurements so the length, sleeve and slit sit exactly where you want them.",
      "Pick a mandarin or band collar, a plain or embroidered placket, and a fabric that suits the occasion — from breathable cottons for daily wear to silks for the wedding season.",
    ],
    faqs: [
      { q: "How long does a bespoke kurta take?", a: "10–12 days from measurement confirmation." },
      { q: "Can I order a matching pyjama or churidar?", a: "Yes — mention it in special requirements and we'll cut it from the same or a complementary fabric." },
      { q: "Do you offer embroidery?", a: "Yes, on the placket and cuff. Add the detail in special requirements; additional charges may apply." },
    ],
  },
};

export const BESPOKE_CATEGORY_LIST = Object.values(BESPOKE_CATEGORIES);
